import { Drawer, Flex, Box, Button } from '@mantine/core';
import { FormInput } from '../../../../components/FormElements/FormInput';
import { useForm } from 'react-hook-form';
import { TbChevronRight } from 'react-icons/tb';
import {
  useCreateGroup,
  useUpdateGroupedWorkflow,
  workflowService
} from '../../../../services/workflow.services';
import { useEffect, useState } from 'react';
import { periodReportTypes } from '../../../../consts/reportTypes';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

/**
 * @param {{
 *   isOpen?: any;
 *   defaultValues?: any;
 *   isEdit?: boolean;
 *   onClose?: any;
 *   workflowIds?: any;
 *   refetch?: any;
 *   onClearGroup?: any;
 *   groupId?: any;
 * }} props
 */
export function MergeAIFM({
  isOpen,
  defaultValues,
  isEdit = false,
  onClose,
  workflowIds,
  refetch,
  onClearGroup,
  groupId
}) {
  const { control, handleSubmit, reset } = useForm();
  const navigate = useNavigate();
  const createGroup = useCreateGroup();
  const [validating, setValidating] = useState(false);

  const updateGroupedWorkflows = useUpdateGroupedWorkflow();

  useEffect(() => {
    if (isEdit && isOpen) {
      reset({
        ...defaultValues
      });
    }
  }, [isOpen]);

  const onSubmit = async data => {
    if (validating || createGroup.isLoading || updateGroupedWorkflows.isLoading) return;
    const ids = isEdit ? defaultValues?.workflow_ids : workflowIds;
    if (!Array.isArray(ids) || !ids.length || new Set(ids).size !== ids.length) {
      toast.error('Select valid, distinct projects to consolidate.');
      return;
    }
    setValidating(true);
    try {
      const workflows = await Promise.all(ids.map(id => workflowService.getWorkflowById(id)));
      const types = workflows.map(response => (response?.data ?? response)?.report_type);
      if (types.some(type => !periodReportTypes.includes(type)) || new Set(types).size !== 1) {
        toast.error(
          'Only projects of the same period-based product can be consolidated. PRIIPs is not supported.'
        );
        return;
      }
    } catch {
      toast.error('Unable to verify the selected projects. Consolidation was not submitted.');
      return;
    } finally {
      setValidating(false);
    }
    if (isEdit) {
      updateGroupedWorkflows.mutate(
        {
          id: groupId,
          body: {
            name: data.name,
            workflow_ids: ids
          }
        },
        {
          onSuccess: () => {
            refetch();
            onClose();
          },
          onError: res => {
            toast.error(res?.data?.detail);
          }
        }
      );
    } else {
      createGroup.mutate(
        {
          name: data.name,
          workflow_ids: ids
        },
        {
          onSuccess: res => {
            navigate(`/project/quaters/${res.id}?type=grouped`);
            onClose();
            refetch();
            onClearGroup();
          },
          onError: res => {
            toast.error(res?.data?.detail);
          }
        }
      );
    }
  };

  return (
    <Drawer
      w="400px"
      title={isEdit ? 'Edit Merged AIFM' : 'Create Merged AIFM'}
      withContainer={false}
      opened={isOpen}
      onClose={onClose}
      position="right"
      overlayProps={{
        display: 'none'
      }}
    >
      <Box pt="16px" component="form" onSubmit={handleSubmit(onSubmit)}>
        <FormInput name="name" control={control} label="Merged AIFM Name" required />
        <Flex mt="48px" justify="flex-end" className="footer">
          <Button
            loading={validating || createGroup.isLoading || updateGroupedWorkflows.isLoading}
            rightSection={<TbChevronRight />}
            size="sm"
            type="submit"
          >
            Continue
          </Button>
        </Flex>
      </Box>
    </Drawer>
  );
}
